import { useState, useEffect } from 'react'
import { TrendingUp, TrendingDown, Minus, ShieldCheck, ShieldAlert, Shield, Info } from 'lucide-react'
import { leadsAPI } from '../api'
import { useTranslation } from 'react-i18next'
import i18n from '../../../i18n'
import { localizeApiDetail } from '../../../i18n/apiError'
import type { RegnskapResponse, RegnskapYear, RegnskapMetric, RegnskapFlag } from '../api'
import LeadsTableExport from '../LeadsTableExport'
import type { ColumnDefinition } from '../../../utils/exportToCsv'

interface Props {
  orgNr: string
  /** For the export's title (#885). */
  orgNavn?: string | null
}

// The six metric keys the API sends; their labels live in leads:regnskap.metrics.
const METRIC_KEYS = ['egenkapitalandel', 'likviditetsgrad', 'resultat', 'driftsmargin', 'gjeldsgrad', 'omsetning'] as const
type MetricKey = (typeof METRIC_KEYS)[number]
const isMetricKey = (key: string): key is MetricKey => (METRIC_KEYS as readonly string[]).includes(key)

type RowKey =
  | 'sumDriftsinntekter' | 'driftsresultat' | 'aarsresultat' | 'sumAnleggsmidler' | 'sumOmloepsmidler'
  | 'sumEiendeler' | 'sumEgenkapital' | 'sumLangsiktigGjeld' | 'sumKortsiktigGjeld' | 'sumGjeld'

// [field key, label key, is sum row]
const PL_LABELS: [string, RowKey, boolean?][] = [
  ['sum_driftsinntekter', 'sumDriftsinntekter'],
  ['driftsresultat', 'driftsresultat'],
  ['aarsresultat', 'aarsresultat', true],
]

const BALANCE_LABELS: [string, RowKey, boolean?][] = [
  ['sum_anleggsmidler', 'sumAnleggsmidler'],
  ['sum_omloepsmidler', 'sumOmloepsmidler'],
  ['sum_eiendeler', 'sumEiendeler', true],
  ['sum_egenkapital', 'sumEgenkapital', true],
  ['sum_langsiktig_gjeld', 'sumLangsiktigGjeld'],
  ['sum_kortsiktig_gjeld', 'sumKortsiktigGjeld'],
  ['sum_gjeld', 'sumGjeld', true],
]

const FLAG_STYLES: Record<RegnskapFlag, string> = {
  green: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-800',
  yellow: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800',
  warning: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-800',
}

const FLAG_DOT: Record<RegnskapFlag, string> = {
  green: 'bg-green-500',
  yellow: 'bg-yellow-500',
  warning: 'bg-red-500',
}

const OVERALL_CONFIG: Record<RegnskapFlag, { icon: typeof ShieldCheck }> = {
  green: { icon: ShieldCheck },
  yellow: { icon: Shield },
  warning: { icon: ShieldAlert },
}

function formatNOK(value: number | null): string {
  if (value === null) return '—'
  return value.toLocaleString('nb-NO', { maximumFractionDigits: 0 })
}

function formatMetricValue(metric: RegnskapMetric): string {
  if (metric.value === null) return 'N/A'
  if (metric.unit === 'NOK') return formatNOK(metric.value)
  if (metric.unit === '%') return `${metric.value}%`
  if (metric.unit === 'x') return `${metric.value}x`
  return String(metric.value)
}

function TrendIcon({ direction }: { direction: string | null }) {
  if (direction === 'up') return <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400 inline" />
  if (direction === 'down') return <TrendingDown className="w-4 h-4 text-red-600 dark:text-red-400 inline" />
  if (direction === 'stable') return <Minus className="w-4 h-4 text-gray-500 inline" />
  return null
}

// egenkapital + gjeld — the balance total, which must equal sum_eiendeler
function balanseTotal(y: RegnskapYear): number | null {
  const { sum_egenkapital, sum_gjeld } = y.raw
  if (sum_egenkapital === null || sum_gjeld === null) return null
  return sum_egenkapital + sum_gjeld
}

function RawRow({ label, years, getValue, isSum }: { label: string; years: RegnskapYear[]; getValue: (y: RegnskapYear) => number | null; isSum?: boolean }) {
  const rowClass = isSum
    ? 'border-t border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-dark-border/30'
    : ''
  const cellClass = isSum
    ? 'font-semibold text-gray-900 dark:text-white'
    : 'text-gray-600 dark:text-gray-400'
  return (
    <tr className={rowClass}>
      <td className={`text-sm ${cellClass}`}>{label}</td>
      {years.map((y, i) => {
        const val = getValue(y)
        const prevYear = years[i + 1]
        const prevVal = prevYear ? getValue(prevYear) : null
        let arrow: string | null = null
        if (val !== null && prevVal !== null && prevVal !== 0) {
          const pct = ((val as number) - (prevVal as number)) / Math.abs(prevVal as number)
          if (pct > 0.10) arrow = 'up'
          else if (pct < -0.10) arrow = 'down'
          else arrow = 'stable'
        }
        return (
          <td key={y.year} className={`text-right text-sm font-mono ${cellClass}`}>
            {formatNOK(val as number | null)}
            {arrow && <span className="ml-1"><TrendIcon direction={arrow} /></span>}
          </td>
        )
      })}
    </tr>
  )
}

export default function RegnskapTab({ orgNr, orgNavn }: Props) {
  const { t } = useTranslation('leads')
  const [data, setData] = useState<RegnskapResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    setError(null)
    leadsAPI.getRegnskap(orgNr)
      .then(res => setData(res.data))
      .catch(err => {
        if (err.response?.status === 502) {
          setError(i18n.t('regnskap.loadFailed', { ns: 'leads' }))
        } else {
          setError(localizeApiDetail(err.response?.data?.detail) || i18n.t('regnskap.genericError', { ns: 'leads' }))
        }
      })
      .finally(() => setLoading(false))
  }, [orgNr])

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse h-16 bg-gray-100 dark:bg-dark-border rounded-sm" />
        <div className="animate-pulse h-48 bg-gray-100 dark:bg-dark-border rounded-sm" />
      </div>
    )
  }

  if (error) {
    return <div className="card p-6 text-red-600 dark:text-red-400">{error}</div>
  }

  if (!data || data.years.length === 0) {
    return (
      <div className="card p-6 text-gray-500 dark:text-gray-400">
        <Info className="w-5 h-5 inline mr-2" />
        {t('regnskap.empty')}
      </div>
    )
  }

  const latest = data.years[0]

  // The year overview as rows of [post, one value per year], in the table's
  // order. Raw numbers, so Excel can sum them.
  const exportRows = [...PL_LABELS, ...BALANCE_LABELS].map(([key, label]) => ({
    post: t(`regnskap.rows.${label}`),
    ...Object.fromEntries(data.years.map(y => [`y${y.year}`, y.raw[key as keyof typeof y.raw]])),
  }))
  exportRows.push({
    post: t('regnskap.rows.sumEgenkapitalOgGjeld'),
    ...Object.fromEntries(data.years.map(y => [`y${y.year}`, balanseTotal(y)])),
  })
  const exportColumns: ColumnDefinition[] = [
    { key: 'post', label: t('regnskap.post') },
    ...data.years.map(y => ({ key: `y${y.year}`, label: String(y.year), format: (v: number | null) => (v == null ? '—' : String(v)) })),
  ]
  const overall = OVERALL_CONFIG[latest.overall]
  const OverallIcon = overall.icon

  return (
    <div className="space-y-6">
      {/* Overall banner */}
      <div className={`rounded-lg border p-4 flex items-center gap-3 ${FLAG_STYLES[latest.overall]}`}>
        <OverallIcon className="w-6 h-6 shrink-0" />
        <div>
          <div className="font-bold text-sm">{t(`regnskap.overall.${latest.overall}`)}</div>
          <div className="text-xs mt-0.5 opacity-75">
            {t('regnskap.basedOn', { year: latest.year })}
            {latest.revidert && t('regnskap.revidertSuffix')}
            {data.trend.omsetning && (
              <span className="ml-2">
                {t('regnskap.omsetning')} <TrendIcon direction={data.trend.omsetning} />
              </span>
            )}
            {data.trend.resultat && (
              <span className="ml-2">
                {t('regnskap.resultat')} <TrendIcon direction={data.trend.resultat} />
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {Object.entries(latest.metrics).map(([key, metric]) => {
          const label = isMetricKey(key)
            ? { name: t(`regnskap.metrics.${key}.name`), desc: t(`regnskap.metrics.${key}.desc`) }
            : undefined
          return (
            <div key={key} className="card p-4 border">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500 dark:text-gray-400">{label?.name ?? key}</span>
                <span className={`w-2.5 h-2.5 rounded-full ${FLAG_DOT[metric.flag]}`} />
              </div>
              <div className="text-lg font-bold text-gray-900 dark:text-white">
                {formatMetricValue(metric)}
              </div>
              <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                {label?.desc}
              </div>
            </div>
          )
        })}
      </div>

      {/* Year comparison table */}
      <div className="card p-6">
        <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3">{t('regnskap.yearOverview')}</h3>
        <LeadsTableExport
          rows={exportRows}
          columns={exportColumns}
          title={t('tableExport.orgTitle', { table: t('regnskap.yearOverview'), navn: orgNavn ?? orgNr, orgNr })}
          testId="bedrift-regnskap-export"
        />
        <div className="overflow-x-auto">
          <table className="data-table data-table-compact">
            <thead>
              <tr>
                <th className="text-left">{t('regnskap.post')}</th>
                {data.years.map(y => (
                  <th key={y.year} className="text-right">{y.year}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* P&L section */}
              <tr>
                <td colSpan={data.years.length + 1} className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide pt-2 pb-1">{t('regnskap.resultatregnskap')}</td>
              </tr>
              {PL_LABELS.map(([key, label, isSum]) => (
                <RawRow
                  key={key}
                  label={t(`regnskap.rows.${label}`)}
                  years={data.years}
                  isSum={isSum}
                  getValue={y => y.raw[key as keyof typeof y.raw]}
                />
              ))}
              {/* Balance section */}
              <tr>
                <td colSpan={data.years.length + 1} className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide pt-4 pb-1 border-t border-gray-200 dark:border-gray-700">{t('regnskap.balanse')}</td>
              </tr>
              {BALANCE_LABELS.map(([key, label, isSum]) => (
                <RawRow
                  key={key}
                  label={t(`regnskap.rows.${label}`)}
                  years={data.years}
                  isSum={isSum}
                  getValue={y => y.raw[key as keyof typeof y.raw]}
                />
              ))}
              <RawRow
                label={t('regnskap.rows.sumEgenkapitalOgGjeld')}
                years={data.years}
                isSum
                getValue={balanseTotal}
              />
            </tbody>
          </table>
        </div>
        <div className="mt-3 text-xs text-gray-400 dark:text-gray-500 flex gap-4">
          {latest.regnskapsregler && <span>{t('regnskap.regler', { v: latest.regnskapsregler })}</span>}
          <span>{latest.revidert ? t('regnskap.revidert') : t('regnskap.ikkeRevidert')}</span>
          {latest.period.fra && <span>{t('regnskap.periode', { fra: latest.period.fra, til: latest.period.til })}</span>}
        </div>
      </div>
    </div>
  )
}
